import { ReactNode, useMemo } from 'react';
import { Virtuoso } from 'react-virtuoso';

import { MessageContentPart, S3DataSchema } from '@/types/schema';
import { cn } from '@/utils/tailwind';

import { PlaygroundOutputCodeBlock } from '../../Pages/Playground/components/PlaygroundOutputCodeBlock';
import {
  OpenAIFunctionCallKwargsSchema,
  OpenAIToolCallsKwargsSchema,
  ToolCallsKwargsSchema,
} from '../Message.utils';
import { TextWithLinks } from '../TextWithLinks';
import { ContentPartRenderer } from './ContentPartRenderer';

// Virtualized content item types
interface VirtualizedTextItem {
  type: 'text_chunk';
  content: string;
  originalIndex: number;
}

interface VirtualizedNonTextItem {
  type: 'non_text';
  content: MessageContentPart;
  originalIndex: number;
}

type VirtualizedContentItem = VirtualizedTextItem | VirtualizedNonTextItem;

// Chunk text with line break awareness
function chunkText(text: string, chunkSize: number = 5000): string[] {
  if (!text) return [];

  const result: string[] = [];
  let currentPosition = 0;

  while (currentPosition < text.length) {
    let endPosition = Math.min(currentPosition + chunkSize, text.length);

    if (endPosition < text.length) {
      const nextNewline = text.indexOf('\n', endPosition);
      // If a newline is reasonably close, use it as the break point
      if (nextNewline !== -1 && nextNewline - currentPosition < chunkSize * 2) {
        endPosition = nextNewline + 1;
      }
    }

    result.push(text.substring(currentPosition, endPosition));
    currentPosition = endPosition;
  }

  return result;
}

// Check if content part is a text type
function isTextContentPart(part: MessageContentPart | string): boolean {
  if (typeof part === 'string') return true;
  return ['text', 'text_delta', 'output_text', 'input_text'].includes(
    part.type
  );
}

// Extract text content from a content part
function extractTextFromPart(part: MessageContentPart | string): string {
  if (typeof part === 'string') return part.trim();

  // Handle text types with string text property
  if (isTextContentPart(part) && 'text' in part) {
    if (typeof part.text === 'string') {
      return part.text.trim();
    }
    // Handle text_delta with object value
    if (typeof part.text === 'object' && part.text && 'value' in part.text) {
      return (part.text as { value: string }).value + '\n';
    }
  }

  return '';
}

// Process content for virtualization
function processContentForVirtualization(
  content: MessageContentPart[]
): VirtualizedContentItem[] {
  const virtualizedItems: VirtualizedContentItem[] = [];
  let i = 0;

  while (i < content.length) {
    const currentPart = content[i];

    if (isTextContentPart(currentPart)) {
      // Collect consecutive text parts
      let combinedText = '';
      const startIndex = i;

      while (i < content.length && isTextContentPart(content[i])) {
        const textContent = extractTextFromPart(content[i]);
        if (textContent) combinedText += textContent;
        i++;
      }

      // Chunk the combined text if it's not empty
      if (combinedText) {
        chunkText(combinedText).forEach((chunk) => {
          virtualizedItems.push({
            type: 'text_chunk',
            content: chunk,
            originalIndex: startIndex,
          });
        });
      }
    } else {
      // Non-text content - keep as individual items
      virtualizedItems.push({
        type: 'non_text',
        content: currentPart,
        originalIndex: i,
      });
      i++;
    }
  }

  return virtualizedItems;
}

function RenderToolCalls({
  toolCall,
  fallback,
}: {
  toolCall?: any;
  fallback?: any;
}) {
  const fnCall = OpenAIFunctionCallKwargsSchema.safeParse(toolCall);

  if (fnCall.success) {
    return (
      <PlaygroundOutputCodeBlock
        code={JSON.stringify(fnCall.data, null, 2)}
        language={'json'}
      />
    );
  }

  const oaiToolCalls = OpenAIToolCallsKwargsSchema.safeParse(toolCall);
  if (oaiToolCalls.success) {
    return (
      <PlaygroundOutputCodeBlock
        code={JSON.stringify(oaiToolCalls.data, null, 2)}
        language={'json'}
      />
    );
  }

  const toolCalls = ToolCallsKwargsSchema.safeParse(toolCall);
  if (toolCalls.success && toolCalls.data.tool_calls.length > 0) {
    return (
      <PlaygroundOutputCodeBlock
        code={JSON.stringify(toolCalls.data, null, 2)}
        language={'json'}
      />
    );
  }

  return (
    <PlaygroundOutputCodeBlock
      code={JSON.stringify(toolCall ?? fallback, null, 2)}
      language={'json'}
    />
  );
}

// Render virtualized content using react-virtuoso
export function VirtualizedContentRenderer(props: {
  content: MessageContentPart[];
  toolCalls?: any;
  attachments?: Record<string, S3DataSchema>;
  className?: string;
  showToolCalls?: boolean;
  maxHeightForVirtualization: string;
  fallback?: ReactNode;
}) {
  const virtualizedItems = useMemo(() => {
    return processContentForVirtualization(props.content);
  }, [props.content]);

  const renderVirtualizedItem = (index: number) => {
    const item = virtualizedItems[index];
    if (!item) return null;

    if (item.type === 'text_chunk') {
      return (
        <div
          key={`text-${index}`}
          className={cn('whitespace-pre-wrap text-sm', props.className)}
        >
          <TextWithLinks text={item.content} />
        </div>
      );
    }

    if (item.type === 'non_text') {
      const part = item.content;
      const idx = item.originalIndex;

      // Handle tool_use separately as it's not part of the shared component
      if (
        typeof part === 'object' &&
        part.type === 'tool_use' &&
        props.showToolCalls
      ) {
        const toolCallFound = props.toolCalls?.find((t) => t.id === part.id);
        const toolCall = toolCallFound
          ? { tool_calls: [toolCallFound] }
          : undefined;
        return <RenderToolCalls toolCall={toolCall} fallback={part} />;
      }

      // Use the shared component for all other content types
      return (
        <ContentPartRenderer
          key={`non-text-${index}`}
          part={part}
          idx={idx}
          attachments={props.attachments}
          className={props.className}
          showToolCalls={props.showToolCalls}
          toolCalls={props.toolCalls}
        />
      );
    }

    return null;
  };

  if (virtualizedItems.length < 10 && props.fallback) {
    return props.fallback;
  }

  return (
    <div style={{ height: props.maxHeightForVirtualization }}>
      <Virtuoso
        totalCount={virtualizedItems.length}
        itemContent={renderVirtualizedItem}
        increaseViewportBy={500}
      />
    </div>
  );
}
